/**
 * Animation Recorder Component
 *
 * Allows users to record custom animations by dragging bones.
 * Outputs BoneAnimation[] directly for saving as RigAction.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  CharacterRig,
  BoneAnimation,
  BoneKeyframe,
} from '../../types/RiggingTypes';
import { ActionRegistry } from '../../services/ActionRegistry';
import { resolveCharacterImageUrl } from '../../services/characterStorageService';
import {
  FiberManualRecord,
  Stop,
  PlayArrow,
  Save,
  Close,
} from '@mui/icons-material';
import './AnimationRecorder.css';

interface AnimationRecorderProps {
  character: CharacterRig;
  onSave: () => void;
  onCancel: () => void;
  videoId?: string;
}

interface RecordedKeyframe {
  boneName: string;
  time: number; // milliseconds from start
  rotation: number;
  translation: { x: number; y: number };
}

interface BoneState {
  rotation: number;
  translation: { x: number; y: number };
}

const AnimationRecorder: React.FC<AnimationRecorderProps> = ({
  character,
  onSave,
  onCancel,
  videoId,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordedKeyframes, setRecordedKeyframes] = useState<
    RecordedKeyframe[]
  >([]);
  const recordingStartTime = useRef<number>(0);

  // Bone manipulation state
  const [boneStates, setBoneStates] = useState<Map<string, BoneState>>(
    new Map()
  );
  const [selectedBone, setSelectedBone] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const initialRotation = useRef<number>(0);

  // Save dialog state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [actionName, setActionName] = useState('');
  const [actionKeywords, setActionKeywords] = useState('');

  // Playback refs
  const playbackRef = useRef<number | null>(null);
  const playbackStartTime = useRef<number>(0);

  // Initialize bone states
  useEffect(() => {
    const initial = new Map<string, BoneState>();
    character.bones.forEach((bone) => {
      initial.set(bone.name, { rotation: 0, translation: { x: 0, y: 0 } });
    });
    setBoneStates(initial);
  }, [character]);

  // Load image
  useEffect(() => {
    const img = new Image();
    let cancelled = false;
    img.onload = () => {
      if (cancelled) return;
      imageRef.current = img;
      drawCanvas();
    };

    // The stored imageUrl may be a relative blob path; resolve it with the
    // user SAS token before loading.
    resolveCharacterImageUrl(character.imageUrl)
      .then((url) => {
        if (!cancelled && url) img.src = url;
      })
      .catch(() => {
        /* keep the preview blank on resolution failure */
      });

    return () => {
      cancelled = true;
      img.onload = null;
    };
  }, [character.imageUrl]);

  // Draw canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate image positioning
    const scale = Math.min(
      (canvas.width * 0.8) / img.width,
      (canvas.height * 0.8) / img.height
    );
    const imgWidth = img.width * scale;
    const imgHeight = img.height * scale;
    const imgX = (canvas.width - imgWidth) / 2;
    const imgY = (canvas.height - imgHeight) / 2;

    // Save context for transformations
    ctx.save();
    ctx.translate(imgX + imgWidth / 2, imgY + imgHeight / 2);

    // Draw image at center
    ctx.drawImage(img, -imgWidth / 2, -imgHeight / 2, imgWidth, imgHeight);

    ctx.restore();

    // Helper to convert normalized position to canvas position
    const toCanvasPos = (pos: { x: number; y: number }) => ({
      x: imgX + pos.x * imgWidth,
      y: imgY + pos.y * imgHeight,
    });

    // Draw bones with current rotations
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';

    character.bones.forEach((bone) => {
      const startJoint = character.joints.find(
        (j) => j.id === bone.startJointId
      );
      const endJoint = character.joints.find((j) => j.id === bone.endJointId);
      if (!startJoint || !endJoint) return;

      const start = toCanvasPos(startJoint.position);
      const end = toCanvasPos(endJoint.position);
      const state = boneStates.get(bone.name) || {
        rotation: 0,
        translation: { x: 0, y: 0 },
      };

      // Calculate rotated end position
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      const baseAngle = Math.atan2(dy, dx);
      const rotatedAngle = baseAngle + (state.rotation * Math.PI) / 180;

      const rotatedEnd = {
        x:
          start.x +
          Math.cos(rotatedAngle) * length +
          state.translation.x * scale,
        y:
          start.y +
          Math.sin(rotatedAngle) * length +
          state.translation.y * scale,
      };

      // Draw bone
      const isSelected = selectedBone === bone.name;
      ctx.strokeStyle = isSelected ? '#36cedc' : 'rgba(255, 255, 255, 0.8)';
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(rotatedEnd.x, rotatedEnd.y);
      ctx.stroke();

      // Draw joint circles
      ctx.fillStyle = isSelected ? '#36cedc' : '#fff';
      ctx.beginPath();
      ctx.arc(start.x, start.y, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(rotatedEnd.x, rotatedEnd.y, 6, 0, Math.PI * 2);
      ctx.fill();
    });

    // Draw recording indicator
    if (isRecording) {
      ctx.fillStyle = '#ff4444';
      ctx.beginPath();
      ctx.arc(canvas.width - 30, 30, 10, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [character, boneStates, selectedBone, isRecording]);

  // Redraw on state changes
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Handle mouse down
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calculate image bounds
    const scale = Math.min(
      (canvas.width * 0.8) / img.width,
      (canvas.height * 0.8) / img.height
    );
    const imgWidth = img.width * scale;
    const imgHeight = img.height * scale;
    const imgX = (canvas.width - imgWidth) / 2;
    const imgY = (canvas.height - imgHeight) / 2;

    const toCanvasPos = (pos: { x: number; y: number }) => ({
      x: imgX + pos.x * imgWidth,
      y: imgY + pos.y * imgHeight,
    });

    // Find clicked bone
    let clickedBone: string | null = null;
    for (const bone of character.bones) {
      const startJoint = character.joints.find(
        (j) => j.id === bone.startJointId
      );
      const endJoint = character.joints.find((j) => j.id === bone.endJointId);
      if (!startJoint || !endJoint) continue;

      const start = toCanvasPos(startJoint.position);
      const end = toCanvasPos(endJoint.position);

      // Check distance to line segment
      const dist = distanceToLineSegment(x, y, start.x, start.y, end.x, end.y);
      if (dist < 15) {
        clickedBone = bone.name;
        break;
      }
    }

    if (clickedBone) {
      setSelectedBone(clickedBone);
      setIsDragging(true);
      dragStart.current = { x, y };
      const state = boneStates.get(clickedBone);
      initialRotation.current = state?.rotation || 0;
    }
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || !selectedBone || !dragStart.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;

    // Calculate rotation based on drag
    const deltaX = x - dragStart.current.x;
    const newRotation = initialRotation.current + deltaX * 0.5; // 0.5 degrees per pixel

    setBoneStates((prev) => {
      const next = new Map(prev);
      const current = next.get(selectedBone) || {
        rotation: 0,
        translation: { x: 0, y: 0 },
      };
      next.set(selectedBone, { ...current, rotation: newRotation });
      return next;
    });

    // Record keyframe if recording
    if (isRecording) {
      const elapsed = Date.now() - recordingStartTime.current;
      const state = boneStates.get(selectedBone) || {
        rotation: 0,
        translation: { x: 0, y: 0 },
      };
      setRecordedKeyframes((prev) => [
        ...prev,
        {
          boneName: selectedBone,
          time: elapsed,
          rotation: newRotation,
          translation: state.translation,
        },
      ]);
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
    dragStart.current = null;
  };

  // Start recording
  const startRecording = () => {
    setRecordedKeyframes([]);
    recordingStartTime.current = Date.now();
    setIsRecording(true);

    // Capture initial state of all bones
    boneStates.forEach((state, boneName) => {
      setRecordedKeyframes((prev) => [
        ...prev,
        {
          boneName,
          time: 0,
          rotation: state.rotation,
          translation: state.translation,
        },
      ]);
    });
  };

  // Stop recording
  const stopRecording = () => {
    setIsRecording(false);

    // Capture final state
    const elapsed = Date.now() - recordingStartTime.current;
    boneStates.forEach((state, boneName) => {
      setRecordedKeyframes((prev) => [
        ...prev,
        {
          boneName,
          time: elapsed,
          rotation: state.rotation,
          translation: state.translation,
        },
      ]);
    });
  };

  // Play recorded animation
  const playAnimation = () => {
    if (recordedKeyframes.length === 0) return;

    setIsPlaying(true);
    playbackStartTime.current = Date.now();

    const maxTime = Math.max(...recordedKeyframes.map((k) => k.time));

    const animate = () => {
      const elapsed = Date.now() - playbackStartTime.current;
      const progress = elapsed / maxTime;

      if (progress >= 1) {
        setIsPlaying(false);
        return;
      }

      // Interpolate bone states
      const currentTime = elapsed;
      const newStates = new Map<string, BoneState>();

      character.bones.forEach((bone) => {
        const boneKeyframes = recordedKeyframes
          .filter((k) => k.boneName === bone.name)
          .sort((a, b) => a.time - b.time);

        if (boneKeyframes.length === 0) {
          newStates.set(bone.name, {
            rotation: 0,
            translation: { x: 0, y: 0 },
          });
          return;
        }

        // Find surrounding keyframes
        let before = boneKeyframes[0];
        let after = boneKeyframes[boneKeyframes.length - 1];

        for (let i = 0; i < boneKeyframes.length - 1; i++) {
          if (
            boneKeyframes[i].time <= currentTime &&
            boneKeyframes[i + 1].time >= currentTime
          ) {
            before = boneKeyframes[i];
            after = boneKeyframes[i + 1];
            break;
          }
        }

        // Interpolate
        const t =
          after.time === before.time
            ? 0
            : (currentTime - before.time) / (after.time - before.time);

        newStates.set(bone.name, {
          rotation: before.rotation + (after.rotation - before.rotation) * t,
          translation: {
            x:
              before.translation.x +
              (after.translation.x - before.translation.x) * t,
            y:
              before.translation.y +
              (after.translation.y - before.translation.y) * t,
          },
        });
      });

      setBoneStates(newStates);
      playbackRef.current = requestAnimationFrame(animate);
    };

    animate();
  };

  // Stop playback
  useEffect(() => {
    return () => {
      if (playbackRef.current) {
        cancelAnimationFrame(playbackRef.current);
      }
    };
  }, []);

  // Convert recorded keyframes to BoneAnimation[]
  const convertToBoneAnimations = (): BoneAnimation[] => {
    if (recordedKeyframes.length === 0) return [];

    const maxTime = Math.max(...recordedKeyframes.map((k) => k.time));
    const animations: BoneAnimation[] = [];

    character.bones.forEach((bone) => {
      const boneKeyframes = recordedKeyframes
        .filter((k) => k.boneName === bone.name)
        .sort((a, b) => a.time - b.time);

      if (boneKeyframes.length === 0) return;

      // Deduplicate and normalize times
      const keyframes: BoneKeyframe[] = [];
      let lastRotation: number | null = null;

      boneKeyframes.forEach((kf) => {
        // Skip if same rotation as last
        if (
          lastRotation !== null &&
          Math.abs(kf.rotation - lastRotation) < 0.1
        ) {
          return;
        }

        keyframes.push({
          time: maxTime > 0 ? kf.time / maxTime : 0,
          rotation: kf.rotation,
          translation: kf.translation,
        });
        lastRotation = kf.rotation;
      });

      // Ensure we have at least start and end keyframes
      if (keyframes.length > 0 && keyframes[0].time > 0) {
        keyframes.unshift({
          time: 0,
          rotation: 0,
          translation: { x: 0, y: 0 },
        });
      }
      if (keyframes.length > 0 && keyframes[keyframes.length - 1].time < 1) {
        const last = keyframes[keyframes.length - 1];
        keyframes.push({ ...last, time: 1 });
      }

      if (keyframes.length > 0) {
        animations.push({
          boneId: bone.id,
          boneName: bone.name,
          keyframes,
        });
      }
    });

    return animations;
  };

  // Save action
  const handleSave = () => {
    if (!actionName.trim()) return;

    const boneAnimations = convertToBoneAnimations();
    const maxTime = Math.max(...recordedKeyframes.map((k) => k.time));
    const duration = maxTime / 1000; // Convert to seconds

    const keywords = actionKeywords
      .split(',')
      .map((k) => k.trim().toLowerCase())
      .filter((k) => k.length > 0);

    ActionRegistry.createFromRecording(
      actionName.trim(),
      boneAnimations,
      duration || 1,
      { keywords, videoId }
    );

    setShowSaveDialog(false);
    onSave();
  };

  return (
    <div className="animation-recorder">
      <div className="recorder-canvas-container">
        <canvas
          ref={canvasRef}
          width={600}
          height={500}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      </div>

      <div className="recorder-controls">
        <div className="control-group">
          {!isRecording ? (
            <button
              className="control-btn record"
              onClick={startRecording}
              disabled={isPlaying}
            >
              <FiberManualRecord /> Record
            </button>
          ) : (
            <button className="control-btn stop" onClick={stopRecording}>
              <Stop /> Stop
            </button>
          )}

          <button
            className="control-btn play"
            onClick={playAnimation}
            disabled={
              isRecording || isPlaying || recordedKeyframes.length === 0
            }
          >
            <PlayArrow /> Play
          </button>
        </div>

        <div className="recorder-info">
          {isRecording && (
            <span className="recording-indicator">● Recording...</span>
          )}
          {!isRecording && recordedKeyframes.length > 0 && (
            <span>{recordedKeyframes.length} keyframes captured</span>
          )}
        </div>

        <div className="control-group">
          <button
            className="control-btn save"
            onClick={() => setShowSaveDialog(true)}
            disabled={recordedKeyframes.length === 0}
          >
            <Save /> Save
          </button>
          <button className="control-btn cancel" onClick={onCancel}>
            <Close /> Cancel
          </button>
        </div>
      </div>

      <div className="recorder-sidebar">
        <h4>Bones</h4>
        <div className="bone-list">
          {character.bones.map((bone) => (
            <div
              key={bone.id}
              className={`bone-item ${selectedBone === bone.name ? 'selected' : ''}`}
              onClick={() => setSelectedBone(bone.name)}
            >
              {bone.name}
            </div>
          ))}
        </div>

        <div className="instructions">
          <h4>How to Record</h4>
          <ol>
            <li>Click Record to start</li>
            <li>Drag bones to pose</li>
            <li>Click Stop when done</li>
            <li>Preview with Play</li>
            <li>Save your animation</li>
          </ol>
        </div>
      </div>

      {showSaveDialog && (
        <div
          className="save-dialog-overlay"
          onClick={() => setShowSaveDialog(false)}
        >
          <div className="save-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Save Animation</h3>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={actionName}
                onChange={(e) => setActionName(e.target.value)}
                placeholder="e.g., Custom Wave"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Keywords (optional)</label>
              <input
                type="text"
                value={actionKeywords}
                onChange={(e) => setActionKeywords(e.target.value)}
                placeholder="e.g., wave, greeting, hello"
              />
              <small>Comma-separated, used for text matching</small>
            </div>
            <div className="dialog-buttons">
              <button onClick={() => setShowSaveDialog(false)}>Cancel</button>
              <button
                className="primary"
                onClick={handleSave}
                disabled={!actionName.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to calculate distance from point to line segment
function distanceToLineSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) param = dot / lenSq;

  let xx, yy;

  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = px - xx;
  const dy = py - yy;
  return Math.sqrt(dx * dx + dy * dy);
}

export default AnimationRecorder;
